import { useSelfHiringContext } from "@/app/contexts/SelfHiringContext";
import type { requestWithdrawBody } from "@/app/services/transactionService/requestWithdraw";
import debounce from "lodash.debounce";
import { parse, format, isValid as isValidDate } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import masks from "@/app/utils/masks";
import { PartnerType } from "@/app/utils/partners.enum";
import { useToast } from "@/view/components/ui/use-toast";
import { useSelfHiringMutation } from "@/app/hooks/useSelfHiringMutation";
import { selfHireService } from "@/app/services/selfHireService";

export type InstallmentOption = { prazo: number; valor: number };

function findClosestAllowedValue(value: number, allowedValues: number[]) {
  if (allowedValues.length === 0) return value;
  return allowedValues.reduce((prev, curr) =>
    Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev,
  );
}

function fieldPathAdicional(convenioDadosId: number) {
  return `crefaz.adicionais.${convenioDadosId}` as const;
}

export const useSelectValues = (form: UseFormReturn<any>) => {
  const { linkName } = useParams();
  const { crefazData, setCrefazData, setStep2mode, seller } =
    useSelfHiringContext();
  const proposalIdCrefaz = crefazData?.proposalData?.propostaId;

  const { createCustomerMutation } = useSelfHiringMutation();

  const { setValue, control, getValues } = form;

  const selectedValue = useWatch({ control, name: "crefaz.value" });
  const selectedInstallment = useWatch({
    control,
    name: "crefaz.installment",
  }) as InstallmentOption | undefined;

  const { toast } = useToast();

  const [inputDigits, setInputDigits] = useState("0");
  const [installments, setInstallments] = useState<InstallmentOption[]>([]);
  const [loadingInstallments, setLoadingInstallments] = useState(false);

  const [isSendingData, setIsSendingData] = useState(false);

  const offersData = crefazData?.offersData;
  const isLoadingOffersData = !offersData;

  const offer = offersData?.offers?.[0];

  const handleSelectOffer = useCallback(
    async (valor: number) => {
      const first = offersData?.offers?.[0];
      const propostaId = proposalIdCrefaz;
      if (!first || !propostaId || !offersData?.proposta || !linkName) return;

      setLoadingInstallments(true);
      try {
        void valor;
        const response = await selfHireService.selectOfferCrefaz(linkName, {
          propostaId,
          produtoId: first.produtoId,
          convenioId: first.convenioId,
          tabelaJurosId: first.tabelaJurosId,
          renda: offersData.proposta.valorRendaPresumida,
          valor,
          tipoCalculo: 0,
        });

        const list = response?.data?.prazoValor as
          | InstallmentOption[]
          | undefined;
        if (list?.length) {
          setInstallments(list);
          const saved = getValues("crefaz.installment");
          const match = saved && list.find((opt) => opt.prazo === saved.prazo);
          const chosen = match ?? list[0];
          setValue("crefaz.installment", chosen);
        } else {
          setInstallments([]);
          setValue("crefaz.installment", null);
        }
      } catch (err) {
        console.error("Erro ao selecionar oferta:", err);
      } finally {
        setLoadingInstallments(false);
      }
    },
    [offersData, proposalIdCrefaz, linkName, getValues, setValue],
  );

  const debouncedFetchInstallments = useCallback(
    debounce((valor: number) => {
      void handleSelectOffer(valor);
    }, 500),
    [handleSelectOffer],
  );

  useEffect(() => {
    const first = offersData?.offers?.[0];
    if (!first || !offersData?.proposta || !proposalIdCrefaz) return;

    const valoresOrdenados = [...first.valores].sort(
      (a, b) => b.valor - a.valor,
    );
    const defaultValor = valoresOrdenados[0]?.valor ?? 0;
    const allowed = first.valores.map((v) => v.valor);

    const current = getValues("crefaz.value");
    const n = Number(current);
    const hasStored =
      current != null &&
      current !== "" &&
      !Number.isNaN(n) &&
      n > 0 &&
      allowed.some((v) => Math.abs(v - n) < 1e-6);

    if (hasStored) {
      setInputDigits(Math.round(n * 100).toString());
      void handleSelectOffer(n);
      return;
    }

    setValue("crefaz.value", defaultValor);
    setInputDigits(Math.round(defaultValor * 100).toString());
    void handleSelectOffer(defaultValor);
  }, [offersData, proposalIdCrefaz, handleSelectOffer, getValues, setValue]);

  const handleSliderChange = (value: number) => {
    if (!offer) return;
    const allowedValues = offer.valores.map((v) => v.valor);
    const closestValue = findClosestAllowedValue(value, allowedValues);
    setValue("crefaz.value", closestValue);
    setInputDigits(Math.round(closestValue * 100).toString());
    debouncedFetchInstallments(closestValue);
  };

  const onSelectInstallment = (item: InstallmentOption) => {
    setValue("crefaz.installment", item);
  };

  const validateAdicionais = (): boolean => {
    if (!offer?.adicionais?.length) return true;

    let isValid = true;
    offer.adicionais.forEach((adic) => {
      const fieldName = fieldPathAdicional(adic.convenioDadosId);
      const value = form.getValues(fieldName as any);
      if (!value || String(value).trim() === "") {
        form.setError(fieldName as any, {
          type: "manual",
          message: "Campo obrigatório",
        });
        isValid = false;
      } else {
        form.clearErrors(fieldName as any);
      }
    });

    return isValid;
  };

  const buildAdicionaisPayload = (): requestWithdrawBody["adicionais"] => {
    if (!offer?.adicionais?.length) return [];

    return offer.adicionais.map((adic) => {
      const fieldName = fieldPathAdicional(adic.convenioDadosId);
      let valor = form.getValues(fieldName as any) as string | undefined;

      if (adic.nome?.toLowerCase?.() === "data de leitura" && valor) {
        const parsedDate = parse(valor, "dd/MM/yyyy", new Date());
        valor = isValidDate(parsedDate)
          ? format(parsedDate, "yyyy-MM-dd")
          : valor;
      }

      return {
        name: adic.nome,
        convenioDadosId: adic.convenioDadosId,
        valor: String(valor ?? ""),
        convenioId: offer.convenioId,
      };
    });
  };
  const advanceToDocuments = form.handleSubmit(async (data: any) => {
    try {
      setIsSendingData(true);

      if (!validateAdicionais()) return;
      if (!offer) return;

      const customerPayload = {
        name: data.name,
        email: data.email,
        cpf: data.cpf,
        phone: masks.unmask(data.phone),
        birthDate: parse(
          data.birthDate,
          "dd/MM/yyyy",
          new Date(),
        ).toISOString(),
      };

      const customerResponse =
        await createCustomerMutation.mutateAsync(customerPayload);

      const withdrawPayload = {
        linkName: linkName || "",
        type: PartnerType.CREFAZ,
        value: data.crefaz.value * 100,
        customerId: customerResponse._id,
        Prazo: data.crefaz.installment.prazo,
        propostaId: proposalIdCrefaz as string,
        prestacao: data.crefaz.installment.valor,
        adicionais: buildAdicionaisPayload(),
        sellerId: seller?._id || "",
      };

      const createTransactionResponse =
        await selfHireService.createCrefazTransaction(withdrawPayload);
      setCrefazData((prev: any) => {
        return {
          transactionId: createTransactionResponse._id,
          proposalData: {
            ...(prev?.proposalData || {}),
            value: data.crefaz.value * 100,
            prestacao: data.crefaz.installment.valor,
            prazo: data.crefaz.installment.prazo,
          },
        };
      });
      setStep2mode("documents");
    } catch (error: any) {
      toast({
        title: "Erro ao processar proposta",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Erro inesperado. Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setIsSendingData(false);
    }
  });

  return {
    form,
    advanceToDocuments,
    offer,
    offersData,
    isLoadingOffersData,
    selectedValue,
    inputDigits,
    setInputDigits,
    handleSliderChange,
    installments,
    selectedInstallment,
    loadingInstallments,
    onSelectInstallment,
    isSendingData,
  };
};
